// Localização: frontend/src/hooks/useDashboardLayout.js
// Hook para gerenciar o layout do dashboard com persistência

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'dashboard_financeiro_layout';

// Layout padrão dos widgets - Estrutura rígida baseada em 1/4 (4 colunas)
// Grid de 4 colunas para garantir estrutura rígida e evitar quebras
// IMPORTANTE: y + h não deve sobrepor outros widgets na mesma coluna
const DEFAULT_LAYOUT = [
    // Linha 0: Cards informativos (1/4 cada) na primeira linha
    // card-saldo: y: 0, h: 2 (ocupa linhas 0-1) - mesmo tamanho dos outros cards
    { i: 'card-saldo', x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    // card-receita-total: y: 0, h: 2 (ocupa linhas 0-1)
    { i: 'card-receita-total', x: 1, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    // card-despesa-total: y: 0, h: 2 (ocupa linhas 0-1)
    { i: 'card-despesa-total', x: 2, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    // card-receitas-pendentes: y: 0, h: 2 (ocupa linhas 0-1)
    { i: 'card-receitas-pendentes', x: 3, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Linha 2: Mais cards e gráfico de tendências
    // card-despesas-pendentes: y: 2, h: 2 (ocupa linhas 2-3)
    { i: 'card-despesas-pendentes', x: 0, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    // card-vencer-7: y: 2, h: 2 (ocupa linhas 2-3)
    { i: 'card-vencer-7', x: 1, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    // card-vencer-30: y: 2, h: 2 (ocupa linhas 2-3)
    { i: 'card-vencer-30', x: 2, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    // card-vencidas: y: 2, h: 2 (ocupa linhas 2-3)
    { i: 'card-vencidas', x: 3, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Gráfico de Tendências - ocupa 2 colunas, começa na linha 4
    { i: 'grafico-tendencias', x: 0, y: 4, w: 2, h: 6, minW: 2, minH: 4 },
    
    // Gráfico Fluxo de Caixa Futuro - ocupa 2 colunas (metade), ao lado direito do gráfico de tendências
    // Começa na mesma linha do gráfico de tendências (y: 4) e na coluna 2
    { i: 'grafico-fluxo-futuro', x: 2, y: 4, w: 2, h: 6, minW: 2, minH: 4 },
    
    // Indicadores de Saúde Financeira - ocupa toda a largura (4/4) - começa após os gráficos acima
    // gráficos acima terminam na linha 9 (y: 4 + h: 6 - 1 = 9), então começa na linha 10
    { i: 'indicadores-saude', x: 0, y: 10, w: 4, h: 4, minW: 2, minH: 3 },
    
    // Gráfico de Vencimentos - ocupa metade (2/4) - começa após indicadores terminar
    // indicadores-saude termina na linha 13 (y: 10 + h: 4 - 1 = 13), então começa na linha 14
    { i: 'grafico-vencimentos', x: 0, y: 14, w: 2, h: 7, minW: 2, minH: 5 },
    
    // Gráfico Comparativo - ocupa metade (2/4) - mesma linha do gráfico de vencimentos
    { i: 'grafico-comparativo', x: 2, y: 14, w: 2, h: 7, minW: 2, minH: 5 },
    
    // Gráfico de Contas a Pagar - ocupa metade (2/4) - começa após os gráficos anteriores
    // gráficos anteriores terminam na linha 20 (y: 14 + h: 7 - 1 = 20), então começa na linha 21
    { i: 'grafico-contas-pagar', x: 0, y: 21, w: 2, h: 7, minW: 2, minH: 5 },
    
    // Gráfico de Contas a Receber - ocupa metade (2/4) - mesma linha do gráfico de contas a pagar
    { i: 'grafico-contas-receber', x: 2, y: 21, w: 2, h: 7, minW: 2, minH: 5 },
    
    // Gráficos de Pizza - separados em dois widgets (2/4 cada) - começam após os gráficos de contas
    // gráficos de contas terminam na linha 27 (y: 21 + h: 7 - 1 = 27), então começam na linha 28
    { i: 'grafico-pizza-receitas', x: 0, y: 28, w: 2, h: 5, minW: 2, minH: 4 },
    { i: 'grafico-pizza-despesas', x: 2, y: 28, w: 2, h: 5, minW: 2, minH: 4 },
];

// Layout específico para mobile (2 colunas) - Cards lado a lado em 2x4
const DEFAULT_LAYOUT_MOBILE = [
    // Linha 0: Primeiros 2 cards lado a lado
    { i: 'card-saldo', x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    { i: 'card-receita-total', x: 1, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Linha 2: Próximos 2 cards lado a lado
    { i: 'card-despesa-total', x: 0, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    { i: 'card-receitas-pendentes', x: 1, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Linha 4: Próximos 2 cards lado a lado
    { i: 'card-despesas-pendentes', x: 0, y: 4, w: 1, h: 2, minW: 1, minH: 2 },
    { i: 'card-vencer-7', x: 1, y: 4, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Linha 6: Últimos 2 cards lado a lado
    { i: 'card-vencer-30', x: 0, y: 6, w: 1, h: 2, minW: 1, minH: 2 },
    { i: 'card-vencidas', x: 1, y: 6, w: 1, h: 2, minW: 1, minH: 2 },
    
    // Gráficos - largura total (2 colunas) no mobile
    { i: 'grafico-tendencias', x: 0, y: 8, w: 2, h: 6, minW: 2, minH: 4 },
    { i: 'grafico-fluxo-futuro', x: 0, y: 14, w: 2, h: 6, minW: 2, minH: 4 },
    { i: 'indicadores-saude', x: 0, y: 20, w: 2, h: 4, minW: 2, minH: 3 },
    { i: 'grafico-vencimentos', x: 0, y: 24, w: 2, h: 7, minW: 2, minH: 5 },
    { i: 'grafico-comparativo', x: 0, y: 31, w: 2, h: 7, minW: 2, minH: 5 },
    { i: 'grafico-contas-pagar', x: 0, y: 38, w: 2, h: 7, minW: 2, minH: 5 },
    { i: 'grafico-contas-receber', x: 0, y: 45, w: 2, h: 7, minW: 2, minH: 5 },
    { i: 'grafico-pizza-receitas', x: 0, y: 52, w: 2, h: 6, minW: 2, minH: 4 },
    { i: 'grafico-pizza-despesas', x: 0, y: 58, w: 2, h: 6, minW: 2, minH: 4 },
];

/**
 * Mescla o layout padrão com o layout salvo
 * Garante que todos os widgets do layout padrão existam
 */
const mergeLayouts = (defaultLayout, savedLayout) => {
    const savedMap = new Map(savedLayout.map(item => [item.i, item]));
    const merged = defaultLayout.map(defaultItem => {
        const saved = savedMap.get(defaultItem.i);
        if (saved) {
            // Preserva posição e tamanho salvos, mas garante minW e minH
            return {
                ...saved,
                minW: defaultItem.minW,
                minH: defaultItem.minH,
            };
        }
        return defaultItem;
    });
    return merged;
};


/**
 * Hook para gerenciar o layout do dashboard financeiro
 * @returns {Object} Objeto com layout, funções de atualização e estado de edição
 */
export const useDashboardLayout = () => {
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Carregar layout salvo do localStorage
    useEffect(() => {
        try {
            const savedLayout = localStorage.getItem(STORAGE_KEY);
            if (savedLayout) {
                const parsed = JSON.parse(savedLayout);
                // Validar se o layout salvo tem todos os widgets necessários
                const mergedLayout = mergeLayouts(DEFAULT_LAYOUT, parsed);
                setLayout(mergedLayout);
            }
        } catch (error) {
            console.error('Erro ao carregar layout do dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Salvar layout no localStorage quando mudar
    useEffect(() => {
        if (!isLoading) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
            } catch (error) {
                console.error('Erro ao salvar layout do dashboard:', error);
            }
        }
    }, [layout, isLoading]);

    /**
     * Atualiza o layout quando um widget é movido ou redimensionado
     * O react-grid-layout pode passar um layout único ou um objeto com layouts por breakpoint
     * O preventCollision=true já garante que não haja sobreposição
     */
    const handleLayoutChange = useCallback((newLayout, allLayouts) => {
        let layoutToProcess = null;
        
        // Se for um array, é um layout único
        if (Array.isArray(newLayout)) {
            layoutToProcess = newLayout;
        } 
        // Se for um objeto, pega o layout do breakpoint atual (lg por padrão)
        else if (allLayouts && allLayouts.lg) {
            layoutToProcess = allLayouts.lg;
        }
        
        if (layoutToProcess) {
            // O react-grid-layout com preventCollision=true já evita sobreposição
            // Apenas salvamos o layout
            setLayout(layoutToProcess);
        }
    }, []);

    /**
     * Alterna o modo de edição
     */
    const toggleEditMode = useCallback(() => {
        setIsEditMode(prev => !prev);
    }, []);

    /**
     * Reseta o layout para o padrão
     */
    const resetLayout = useCallback(() => {
        if (window.confirm('Deseja resetar o layout para o padrão? Esta ação não pode ser desfeita.')) {
            setLayout(DEFAULT_LAYOUT);
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    return {
        layout,
        layoutMobile: DEFAULT_LAYOUT_MOBILE,
        isEditMode,
        isLoading,
        handleLayoutChange,
        toggleEditMode,
        resetLayout,
    };
};

